import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  ChevronRight,
  ChevronLeft,
  MessagesSquare,
  Hash,
  Users,
  MessageCircle,
} from "lucide-react";
import { useSearch } from "./useSearch";
import { useSearchRegistryData } from "./SearchRegistry";
import { SECTION_DEFS } from "../constants";
import { searchMessages } from "../../../api/search";

// ---------------------------------------------------------------------------
// Fuzzy matching
// ---------------------------------------------------------------------------

function fuzzyMatch(text, query) {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();

  if (lower.includes(q)) {
    const idx = lower.indexOf(q);
    return { match: true, score: 100 - idx };
  }

  let qi = 0;
  let score = 0;
  let lastIdx = -1;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) {
      score += 10;
      if (lastIdx === i - 1) score += 5;
      if (i === 0 || lower[i - 1] === " ") score += 8;
      lastIdx = i;
      qi++;
    }
  }

  return qi === q.length ? { match: true, score } : { match: false, score: 0 };
}

function scoreItem(item, query) {
  let best = 0;
  const labelResult = fuzzyMatch(item.label, query);
  if (labelResult.match) best = Math.max(best, labelResult.score);

  for (const kw of item.keywords || []) {
    const kwResult = fuzzyMatch(kw, query);
    if (kwResult.match) best = Math.max(best, kwResult.score * 0.8);
  }

  return best;
}

// ---------------------------------------------------------------------------
// Build top-level items from SECTION_DEFS
// ---------------------------------------------------------------------------

function buildTopLevelItems() {
  return SECTION_DEFS.map((s) => ({
    id: s.path,
    label: s.label,
    keywords: [],
    path: s.path,
    icon: s.Icon,
    category: "Pages",
    hasChildren: s.hasSubItems,
    sectionPath: s.path,
  }));
}

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const BASE_WIDTH = 480;
const COLUMN_WIDTH = 240;
const COLUMN_MIN_WIDTH = 140;
const ACTIVE_MIN_WIDTH = 260;
const MAX_WIDTH = 900;

// ---------------------------------------------------------------------------
// Column panel
// ---------------------------------------------------------------------------

function ColumnPanel({
  items,
  activeIndex,
  isActiveColumn,
  highlightedId,
  onSetActive,
  onExecute,
  onDrill,
  canDrillItem,
  label,
  emptyFlash,
  isKeyboardNav,
  onMouseActivity,
}) {
  const grouped = useMemo(() => {
    const groups = {};
    for (const item of items) {
      const cat = item.category || "Results";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return Object.keys(groups).map((cat) => ({
      category: cat,
      items: groups[cat],
    }));
  }, [items]);

  let runningIndex = 0;

  return (
    <div className="flex flex-col h-full">
      {label && (
        <div className="px-3 pt-2 pb-1 border-b border-mainDivider shrink-0">
          <span className="text-[10px] uppercase tracking-wider font-normal text-mutedText select-none">
            {label}
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto overscroll-contain py-1">
        {items.length === 0 && (
          <div className="px-3 py-6 text-center text-xs font-normal text-mutedText">
            No items
          </div>
        )}

        {grouped.map((group) => {
          const groupStartIndex = runningIndex;
          runningIndex += group.items.length;

          return (
            <div key={group.category}>
              {grouped.length > 1 && (
                <div className="px-3 pt-1.5 pb-0.5">
                  <span className="text-[10px] uppercase tracking-wider font-normal text-mutedText select-none">
                    {group.category}
                  </span>
                </div>
              )}

              {group.items.map((item, i) => {
                const flatIdx = groupStartIndex + i;
                const isActive = isActiveColumn && flatIdx === activeIndex;
                const isHighlighted = highlightedId === item.id;
                const Icon = item.icon;
                const canDrill = canDrillItem(item);
                const showEmptyFlash = isActive && emptyFlash && isActiveColumn;

                return (
                  <button
                    key={item.id}
                    data-active={isActive}
                    tabIndex={-1}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseMove={() => {
                      if (isKeyboardNav && onMouseActivity) onMouseActivity();
                    }}
                    onClick={() => {
                      if (onMouseActivity) onMouseActivity();
                      if (canDrill) {
                        onDrill(item);
                      } else {
                        onExecute(item);
                      }
                    }}
                    onMouseEnter={() => {
                      if (isActiveColumn && !isKeyboardNav)
                        onSetActive(flatIdx);
                    }}
                    className={[
                      "w-full flex items-center gap-x-2.5 px-3 py-1.5 text-left transition-colors duration-100 cursor-pointer rounded-lg",
                      isActive
                        ? "bg-selectedSection text-mainText"
                        : isHighlighted
                          ? "bg-mainButtonBg/50 text-mainText"
                          : "text-mutedText hover:text-mainText hover:bg-mainButtonBg/50",
                    ].join(" ")}
                  >
                    {item.statusDot ? (
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: item.statusDot }}
                      />
                    ) : Icon ? (
                      <Icon
                        strokeWidth={1.5}
                        className={[
                          "h-[15px] w-[15px] shrink-0 transition-colors duration-100",
                          isActive || isHighlighted
                            ? "text-velBlue"
                            : "text-mutedText",
                        ].join(" ")}
                        style={
                          item.iconColor ? { color: item.iconColor } : undefined
                        }
                      />
                    ) : null}
                    {item._backendResult ? (
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-x-1.5">
                          <span className="truncate text-[12px] font-normal tracking-wide">
                            {item.label}
                          </span>
                          <span className="shrink-0 text-[10px] font-normal text-mutedText/50 tracking-wide">
                            {item._typeLabel}
                          </span>
                        </div>
                        <p className="truncate text-[11px] font-normal text-mutedText/70 mt-0.5 leading-tight">
                          {item._senderName ? `${item._senderName}: ` : ""}
                          {item.snippet}
                        </p>
                      </div>
                    ) : (
                      <span className="flex-1 truncate text-[12px] font-normal tracking-wide">
                        {item.label}
                      </span>
                    )}
                    {item.shared && (
                      <span className="shrink-0 text-[10px] font-normal text-mutedText tracking-wide">
                        shared with me
                      </span>
                    )}
                    {showEmptyFlash && (
                      <motion.span
                        key={emptyFlash.key}
                        initial={{ opacity: 0, x: 4 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        className="shrink-0 text-[10px] font-normal text-sunColor"
                      >
                        empty
                      </motion.span>
                    )}
                    {canDrill && !showEmptyFlash && (
                      <ChevronRight
                        strokeWidth={1.5}
                        className={[
                          "h-3 w-3 shrink-0 transition-colors duration-100",
                          isActive || isHighlighted
                            ? "text-mutedText"
                            : "text-mutedText/40",
                        ].join(" ")}
                      />
                    )}
                    {isActive && !canDrill && !showEmptyFlash && (
                      <CornerDownLeft
                        strokeWidth={1.5}
                        className="h-3 w-3 shrink-0 text-mutedText"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function MainSearch() {
  const { isOpen, close } = useSearch();
  const navigate = useNavigate();
  const { store: registry, version: registryVersion } = useSearchRegistryData();
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [drillPath, setDrillPath] = useState([]);
  const [emptyFlash, setEmptyFlash] = useState(null);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  const drillIndexStack = useRef([]);

  // ── Backend message search (debounced) ──────────────────────
  const [backendResults, setBackendResults] = useState([]);
  const [backendLoading, setBackendLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2) {
      setBackendResults([]);
      setBackendLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }

    setBackendLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchMessages(q, 10);
        const data = res?.data || {};
        const items = [];

        // Map chat results (private/public threads)
        for (const r of data.chat_results || []) {
          const typeLabel = r.thread_type === "public" ? "Public" : "Private";
          items.push({
            id: `search-chat-${r.thread_id}`,
            label: r.title || "Untitled Thread",
            keywords: [r.snippet || ""],
            snippet: r.snippet || "",
            category: "Message Content",
            icon: MessagesSquare,
            event: {
              navigateTo: "/chat",
              name: "sidebar:chat-thread-selected",
              detail: { threadId: r.thread_id, thread_type: r.thread_type },
            },
            _backendResult: true,
            _typeLabel: typeLabel,
            _timestamp: r.timestamp || 0,
          });
        }

        // Map team results
        for (const r of data.team_results || []) {
          const convIcon =
            r.conversation_type === "channel"
              ? Hash
              : r.conversation_type === "dm"
                ? MessageCircle
                : Users;
          items.push({
            id: `search-team-${r.team_id}`,
            label: r.title || "Unnamed Conversation",
            keywords: [r.snippet || ""],
            snippet: r.snippet || "",
            category: "Message Content",
            icon: convIcon,
            event: {
              navigateTo: "/chat",
              name: "sidebar:chat-thread-selected",
              detail: {
                threadId: null,
                thread_type: "team",
                team_id: r.team_id,
              },
            },
            _backendResult: true,
            _typeLabel: r.conversation_type || "team",
            _senderName: r.sender_name || "",
            _timestamp: r.timestamp || 0,
          });
        }

        setBackendResults(items);
      } catch (e) {
        console.error("Backend search failed:", e);
        setBackendResults([]);
      } finally {
        setBackendLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const topLevelItems = useMemo(() => buildTopLevelItems(), []);

  const resolveItemsAtLevel = useCallback(
    (level) => {
      if (level === 0) return topLevelItems;

      const sectionPath = drillPath[0]?.sectionPath;
      const portalItems = Array.isArray(registry[sectionPath])
        ? registry[sectionPath]
        : [];
      const sharedKey = `__shared:${sectionPath}`;
      const sharedItems = Array.isArray(registry[sharedKey])
        ? registry[sharedKey]
        : [];
      let items = [...portalItems, ...sharedItems];

      for (let i = 1; i < level; i++) {
        const target = drillPath[i];
        const parent = items.find((item) => item.id === target?.id);
        if (parent?.children) {
          items = parent.children;
        } else {
          break;
        }
      }

      return items;
    },
    [drillPath, topLevelItems, registry, registryVersion],
  );

  const browseItems = useMemo(
    () => resolveItemsAtLevel(drillPath.length),
    [resolveItemsAtLevel, drillPath.length],
  );

  const allSearchableItems = useMemo(() => {
    const items = [...topLevelItems];

    const flatten = (arr, parentLabel, sectionPath) => {
      for (const item of arr) {
        items.push({
          ...item,
          category: item.category || parentLabel,
          _sectionPath: sectionPath,
        });
        if (item.children) {
          flatten(item.children, parentLabel, sectionPath);
        }
      }
    };

    for (const [sectionPath, sectionItems] of Object.entries(registry)) {
      if (!Array.isArray(sectionItems)) continue;
      if (sectionPath.startsWith("__shared:")) continue;
      const section = SECTION_DEFS.find((s) => s.path === sectionPath);
      const defaultLabel = section?.label || "";
      flatten(sectionItems, defaultLabel, sectionPath);
      const sharedKey = `__shared:${sectionPath}`;
      if (Array.isArray(registry[sharedKey])) {
        flatten(registry[sharedKey], defaultLabel, sectionPath);
      }
    }

    return items;
  }, [topLevelItems, registry, registryVersion]);

  const results = useMemo(() => {
    if (!query.trim()) {
      return browseItems.map((item) => ({
        ...item,
        category:
          item.category ||
          (drillPath.length > 0
            ? drillPath[drillPath.length - 1].label
            : "Pages"),
      }));
    }

    const clientResults = allSearchableItems
      .map((item) => ({ ...item, _score: scoreItem(item, query) }))
      .filter((item) => item._score > 0)
      .sort((a, b) => b._score - a._score);

    // Merge backend content search results (dedupe by id)
    const clientIds = new Set(clientResults.map((r) => r.id));
    const uniqueBackend = backendResults.filter((r) => !clientIds.has(r.id));

    return [...clientResults, ...uniqueBackend];
  }, [query, browseItems, allSearchableItems, drillPath, backendResults]);

  const flatItems = useMemo(() => {
    const groups = {};
    for (const item of results) {
      const cat = item.category || "Results";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return Object.keys(groups).flatMap((cat) => groups[cat]);
  }, [results]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
      setDrillPath([]);
      setEmptyFlash(null);
      setIsKeyboardNav(false);
      setBackendResults([]);
      setBackendLoading(false);
      drillIndexStack.current = [];
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  const handleMouseActivity = useCallback(() => {
    setIsKeyboardNav(false);
  }, []);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(0, flatItems.length - 1)));
  }, [flatItems.length]);

  useEffect(() => {
    const activeEl = document.querySelector(
      '[data-search-active-column="true"] [data-active="true"]',
    );
    if (activeEl) activeEl.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  useEffect(() => {
    if (!emptyFlash) return;
    const timer = setTimeout(() => setEmptyFlash(null), 1500);
    return () => clearTimeout(timer);
  }, [emptyFlash]);

  const executeItem = useCallback(
    (item) => {
      if (!item) return;
      close();

      if (item.path) {
        window.dispatchEvent(
          new CustomEvent("search:navigate", { detail: { path: item.path } }),
        );
        navigate(item.path);
      } else if (item.event) {
        if (item.event.navigateTo) {
          window.dispatchEvent(
            new CustomEvent("search:navigate", {
              detail: { path: item.event.navigateTo },
            }),
          );
          navigate(item.event.navigateTo);
        }
        setTimeout(
          () => {
            window.dispatchEvent(
              new CustomEvent(item.event.name, { detail: item.event.detail }),
            );
            if (item.event.then) {
              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent(item.event.then.name, {
                    detail: item.event.then.detail,
                  }),
                );
              }, 60);
            }
          },
          item.event.navigateTo ? 50 : 0,
        );
      }
    },
    [close, navigate],
  );

  const itemHasContent = useCallback(
    (item) => {
      if (item.children && item.children.length > 0) return true;
      if (item.sectionPath) {
        const own = Array.isArray(registry[item.sectionPath])
          ? registry[item.sectionPath].length
          : 0;
        const sharedKey = `__shared:${item.sectionPath}`;
        const shared = Array.isArray(registry[sharedKey])
          ? registry[sharedKey].length
          : 0;
        if (own + shared > 0) return true;
      }
      return false;
    },
    [registry, registryVersion],
  );

  const drillInto = useCallback(
    (item) => {
      if (!itemHasContent(item)) {
        if (item.hasChildren) {
          setEmptyFlash({
            message: `No items in ${item.label}`,
            key: Date.now(),
          });
        }
        return;
      }

      drillIndexStack.current.push(activeIndex);
      setDrillPath((prev) => [
        ...prev,
        {
          sectionPath: item.sectionPath || prev[prev.length - 1]?.sectionPath,
          id: item.id,
          label: item.label,
        },
      ]);
      setActiveIndex(0);
    },
    [itemHasContent, activeIndex],
  );

  const drillBack = useCallback(() => {
    setDrillPath((prev) => prev.slice(0, -1));
    const savedIndex = drillIndexStack.current.pop() ?? 0;
    setActiveIndex(savedIndex);
    setEmptyFlash(null);
  }, []);

  const canDrillItem = useCallback(
    (item) => {
      if (query.trim()) return false;
      return itemHasContent(item);
    },
    [query, itemHasContent],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIsKeyboardNav(true);
        setActiveIndex((prev) => (prev < flatItems.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIsKeyboardNav(true);
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : flatItems.length - 1));
      } else if (e.key === "ArrowRight" && !query.trim()) {
        e.preventDefault();
        setIsKeyboardNav(true);
        const item = flatItems[activeIndex];
        if (item) drillInto(item);
      } else if (
        e.key === "ArrowLeft" &&
        !query.trim() &&
        drillPath.length > 0
      ) {
        e.preventDefault();
        setIsKeyboardNav(true);
        drillBack();
      } else if (e.key === "Backspace" && !query && drillPath.length > 0) {
        e.preventDefault();
        drillBack();
      } else if (e.key === "Enter") {
        e.preventDefault();
        executeItem(flatItems[activeIndex]);
      } else if (
        e.key.length === 1 &&
        !e.metaKey &&
        !e.ctrlKey &&
        document.activeElement !== inputRef.current
      ) {
        inputRef.current?.focus();
      }
    },
    [
      flatItems,
      activeIndex,
      executeItem,
      drillInto,
      drillBack,
      query,
      drillPath,
    ],
  );

  if (!isOpen) return null;

  const isSearching = !!query.trim();
  const desiredWidth = isSearching
    ? BASE_WIDTH
    : BASE_WIDTH + drillPath.length * COLUMN_WIDTH;
  const totalWidth = Math.min(desiredWidth, MAX_WIDTH);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4"
      onClick={close}
    >
      {/* Backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 bg-mainBg/60 backdrop-blur-sm"
      />

      <motion.div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        initial={{ opacity: 0, y: -8, scale: 0.98 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          width: totalWidth,
        }}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        className="relative rounded-lg border border-surfaceBorder bg-popUpbg/90 backdrop-blur-lg shadow-login-panel overflow-hidden flex flex-col"
        style={{ maxWidth: MAX_WIDTH, maxHeight: "60vh" }}
      >
        {/* Search input */}
        <div className="flex items-center gap-x-3 px-4 py-3 border-b border-mainDivider shrink-0">
          <Search
            strokeWidth={1.5}
            className="h-[18px] w-[18px] shrink-0 text-mutedText"
            aria-hidden
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
              if (e.target.value.trim()) setDrillPath([]);
            }}
            placeholder={
              drillPath.length > 0
                ? `Search in ${drillPath[drillPath.length - 1].label}…`
                : "Search pages, actions…"
            }
            className="flex-1 bg-transparent text-sm font-normal tracking-wide text-mainText placeholder:text-mutedText outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:flex items-center gap-x-0.5 text-[11px] font-normal text-mutedText border border-btnBorder rounded-lg px-1.5 py-0.5 select-none">
            esc
          </kbd>
        </div>

        {/* Columns area */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {isSearching ? (
            <div
              className="flex-1 overflow-y-auto overscroll-contain"
              data-search-active-column="true"
            >
              <ColumnPanel
                items={results}
                activeIndex={activeIndex}
                isActiveColumn={true}
                highlightedId={null}
                onSetActive={setActiveIndex}
                onExecute={executeItem}
                onDrill={drillInto}
                canDrillItem={canDrillItem}
                label={null}
                emptyFlash={emptyFlash}
                isKeyboardNav={isKeyboardNav}
                onMouseActivity={handleMouseActivity}
              />
              {backendLoading && (
                <div className="px-3 py-2 text-[11px] font-normal text-mutedText text-center">
                  Searching messages…
                </div>
              )}
            </div>
          ) : (
            <>
              <AnimatePresence initial={false}>
                {drillPath.map((crumb, i) => {
                  const levelItems = resolveItemsAtLevel(i).map((item) => ({
                    ...item,
                    category:
                      item.category ||
                      (i === 0 ? "Pages" : drillPath[i - 1]?.label || ""),
                  }));

                  return (
                    <motion.div
                      key={`col-${i}-${crumb.id}`}
                      initial={{ flexBasis: 0, opacity: 0 }}
                      animate={{ flexBasis: COLUMN_WIDTH, opacity: 1 }}
                      exit={{ flexBasis: 0, opacity: 0 }}
                      transition={{
                        duration: 0.22,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className="shrink overflow-hidden border-r border-mainDivider"
                      style={{ minWidth: COLUMN_MIN_WIDTH }}
                    >
                      <ColumnPanel
                        items={levelItems}
                        activeIndex={-1}
                        isActiveColumn={false}
                        highlightedId={crumb.id}
                        onSetActive={() => {}}
                        onExecute={executeItem}
                        onDrill={(item) => {
                          setDrillPath((prev) => {
                            const newPath = prev.slice(0, i);
                            return [
                              ...newPath,
                              {
                                sectionPath:
                                  item.sectionPath ||
                                  newPath[newPath.length - 1]?.sectionPath,
                                id: item.id,
                                label: item.label,
                              },
                            ];
                          });
                          drillIndexStack.current =
                            drillIndexStack.current.slice(0, i);
                          setActiveIndex(0);
                        }}
                        canDrillItem={canDrillItem}
                        label={i === 0 ? "Pages" : drillPath[i - 1]?.label}
                        isKeyboardNav={isKeyboardNav}
                        onMouseActivity={handleMouseActivity}
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              <motion.div
                key={`active-${drillPath.length}`}
                initial={drillPath.length > 0 ? { opacity: 0, x: 30 } : false}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.18,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                className="flex-1 min-w-0 overflow-hidden"
                style={{ minWidth: ACTIVE_MIN_WIDTH }}
                data-search-active-column="true"
              >
                <ColumnPanel
                  items={results}
                  activeIndex={activeIndex}
                  isActiveColumn={true}
                  highlightedId={null}
                  onSetActive={setActiveIndex}
                  onExecute={executeItem}
                  onDrill={drillInto}
                  canDrillItem={canDrillItem}
                  emptyFlash={emptyFlash}
                  isKeyboardNav={isKeyboardNav}
                  onMouseActivity={handleMouseActivity}
                  label={
                    drillPath.length > 0
                      ? drillPath[drillPath.length - 1].label
                      : null
                  }
                />
              </motion.div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-x-4 px-4 py-2 border-t border-mainDivider shrink-0">
          <span className="flex items-center gap-x-1.5 text-[11px] font-normal text-mutedText select-none">
            <ArrowUp strokeWidth={1.5} className="h-3 w-3" aria-hidden />
            <ArrowDown strokeWidth={1.5} className="h-3 w-3" aria-hidden />
            navigate
          </span>
          {!isSearching && (
            <span className="flex items-center gap-x-1.5 text-[11px] font-normal text-mutedText select-none">
              <ChevronRight strokeWidth={1.5} className="h-3 w-3" aria-hidden />
              <ChevronLeft strokeWidth={1.5} className="h-3 w-3" aria-hidden />
              drill
            </span>
          )}
          <span className="flex items-center gap-x-1.5 text-[11px] font-normal text-mutedText select-none">
            <CornerDownLeft strokeWidth={1.5} className="h-3 w-3" aria-hidden />
            select
          </span>
          <span className="flex items-center gap-x-1.5 text-[11px] font-normal text-mutedText select-none">
            <kbd className="border border-btnBorder rounded-lg px-1 py-px text-[10px]">
              esc
            </kbd>
            close
          </span>
        </div>
      </motion.div>
    </div>
  );
}
